/* eslint-disable no-await-in-loop */
import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import ShadowBlock from '../../../atoms/ShadowBlock';
import { MasterListReportColumns, REPORT_PAGE_SIZE } from './MasterListReport.constants';
import MButton from '../../../MUI/Button/MButton';

import {
  createMasterListReport,
  getMasterListReport,
  getMasterListReportAsExcel,
} from '../../../../store/slices/reportsSlice';
import timeoutPromise from '../../../../utils/timeoutPromise';
import { ReactComponent as ExportIcon } from '../../../../assets/icons/document-download-blue.svg';

import Table from '../../../atoms/Table/Table';
import notificationUtils from '../../../../utils/notificationUtils';
import ProgressModal from '../ProductivityByAttorney/ProgressModal';
import { openOrDownloadBinary } from '../../../../utils/binaryHelpers';
import Dropdown from '../../../atoms/Dropdown';
import useManualPagination from '../../../../hooks/useManualPagination';

const MasterListReport = () => {
  const [isLoading, setLoading] = useState(false);
  const [lastReportId, setLastReportId] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportOptions, setReportOptions] = useState([]);
  const [reportOption, setReportOption] = useState(null);

  const dispatch = useDispatch();

  const getReportData = async () => {
    setLoading(true);

    try {
      const { reportId } = await dispatch(createMasterListReport()).unwrap();
      let done = false;

      while (!done) {
        const {
          reportStatus: { code, message },
          items = [],
        } = await dispatch(getMasterListReport(reportId)).unwrap();
        if (code === 'Completed') {
          done = true;

          setReportData(items);
          setLastReportId(reportId);

          const options = items.map((item) => ({
            label: item.masterListType,
            value: item.masterListType,
          }));

          setReportOptions(options);
          setReportOption(options[0] ?? null);
        } else if (code === 'Error') {
          notificationUtils.error(message);
          done = true;
        } else {
          await timeoutPromise(5000);
        }
      }

      setLoading(false);
      return reportId;
    } catch (e) {
      notificationUtils.error(e.message || 'Something went wrong');
    }
    return null;
  };

  const onExport = async () => {
    let reportId = lastReportId;

    if (!reportId) {
      reportId = await getReportData();
      if (!reportId) {
        return;
      }
    }

    setLoading(true);

    try {
      const data = await dispatch(getMasterListReportAsExcel(reportId)).unwrap();

      openOrDownloadBinary({
        contentType: data.contentType,
        content: data.content,
        fileName: data.originalFileName,
      });
    } catch (e) {
      notificationUtils.error(e.message || 'Something went wrong');
    }

    setLoading(false);
  };

  const rowsAdapter = useMemo(() => {
    if (!reportData || !reportOption) {
      return [];
    }

    return (
      reportData.find((item) => item.masterListType === reportOption?.value)
        ?.masterListReportEntities ?? []
    );
  }, [reportData, reportOption]);

  const reportPagination = useManualPagination({
    data: rowsAdapter,
    pageSize: REPORT_PAGE_SIZE,
  });

  return (
    <Stack pb={20}>
      <ProgressModal isOpen={isLoading} />
      <ShadowBlock>
        <Stack
          direction="row"
          sx={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <MButton onClick={getReportData} disabled={isLoading}>
            Run Report
          </MButton>
          <MButton variant="bordered" startIcon={<ExportIcon />} onClick={onExport}>
            Export Excel
          </MButton>
        </Stack>
      </ShadowBlock>

      {Boolean(reportData) && (
        <Stack mt={4}>
          <ShadowBlock>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h5" fontWeight={600}>
                Master List Report
              </Typography>
            </Box>
            <Stack rowGap={2}>
              <Dropdown
                value={reportOption}
                isSearchable
                label="Master List Type"
                width="326px"
                isColumn
                placeholder="Select Master List Type"
                options={reportOptions}
                onChange={setReportOption}
                isDisabled={isLoading}
              />
              <Table
                columns={MasterListReportColumns}
                rows={reportPagination.rows}
                total={reportPagination.totalRowsCount ?? 0}
                isPagination={reportPagination.isPagination}
                onNextPage={reportPagination.setCurrentPage}
                onPreviousPage={reportPagination.setCurrentPage}
                onGotoPage={reportPagination.setCurrentPage}
                pageSize={REPORT_PAGE_SIZE}
              />
            </Stack>
          </ShadowBlock>
        </Stack>
      )}
    </Stack>
  );
};

export default MasterListReport;
